import type { WbsFlatItem } from '../../../../features/wbs/types';
import { parseISO } from '../../../../utils/date';
import { STATUS, THRESHOLDS, type WbsStatusKind } from './wbsStatus';

/**
 * WBS Status 판정 규칙(개선 버전)
 *
 * 핵심 포인트:
 * - Status는 PV(계획%) / EV(실적%) / 기준일(baseDate)을 기반으로 결정한다.
 * - '임박/예정'은 "아직 착수 전"이며 "EV가 없음(0/null)"이고 "PV도 0"인 경우에만 사용한다.
 *   → PV가 이미 진행 중(>0)인데 EV가 비어있으면, 실제로는 '진행이 있어야 하는데 실적이 없다'는 의미이므로 지연/주의로 보는 게 자연스럽다.
 *
 * 입력:
 * - row: WbsFlatItem
 *   - row.plannedStart / plannedFinish: 'YYYY-MM-DD' 형태 (parseISO 사용)
 *   - row.earnedValue: EV (0~100)
 * - pv: PV (0~100)  ※ 현재는 서버 rollup plannedValue를 넘기는 것을 권장
 * - baseDate: 기준일(오늘 or 기준주 anchor date 등)
 */
export function statusFromRow(row: WbsFlatItem, pv: number | null, baseDate: Date): WbsStatusKind {
    // ─────────────────────────────────────────────
    // 0) 명시 상태가 있으면 최우선 (기존 유지)
    // ─────────────────────────────────────────────
    if (row.status) {
        if (row.status === 'ontrack') return STATUS.ON_TRACK;
        if (row.status === 'risk') return STATUS.RISK;
        if (row.status === 'delay') return STATUS.DELAY;
    }

    // ─────────────────────────────────────────────
    // 1) 입력값 정규화
    // ─────────────────────────────────────────────
    const ev = typeof row.earnedValue === 'number' ? row.earnedValue : null;

    // EV가 null/undefined 또는 0 이하이면 "실적 없음"으로 간주
    const evZeroish = ev == null || ev <= 0;

    // PV가 null/undefined 또는 0 이하이면 "계획 진도 근거 없음/0"으로 간주
    const pvZeroish = pv == null || pv <= 0;

    // 계획 시작/종료(없을 수 있음)
    const start = row.plannedStart ? parseISO(row.plannedStart) : null;
    const finish = row.plannedFinish ? parseISO(row.plannedFinish) : null;

    // 날짜 차이 계산(일 단위)
    const dayMs = 86400000;
    const daysToStart = start != null ? Math.floor((start.getTime() - baseDate.getTime()) / dayMs) : null; // +면 미래, 0이면 오늘, -면 과거
    const daysToFinish = finish != null ? Math.floor((finish.getTime() - baseDate.getTime()) / dayMs) : null;

    // ─────────────────────────────────────────────
    // 2) 착수 전 상태(예정/임박)
    //
    // 조건:
    // - start가 존재하고, 기준일이 start 이전(=daysToStart > 0) 이거나 오늘(=0)인 경우 "착수 전/당일"로 본다.
    // - 단, PV>0 또는 EV>0이면 이미 진행 신호가 있으므로 "예정/임박"으로 먼저 떨어뜨리지 않는다.
    // ─────────────────────────────────────────────
    if (start && daysToStart != null && daysToStart >= 0) {
        // 진행 신호가 전혀 없을 때만 예정/임박 판정
        if (evZeroish && pvZeroish) {
            // 임박: 착수 D-7 이내
            if (daysToStart <= THRESHOLDS.IMPENDING_DAYS) return STATUS.IMPENDING;

            // 예정: 착수까지 7일 초과 남음
            return STATUS.PLANNED;
        }
        // 여기서 return하지 않음: (PV>0 또는 EV>0) → 아래 PV/EV 갭 로직으로 판정
    }

    // ─────────────────────────────────────────────
    // 3) "PV는 진행 중인데 EV가 없음/0"인 경우
    //
    // 의미:
    // - 계획상으로는 어느 정도 진행되어야 하는데, 실적이 입력/집계되지 않았다.
    // - 특히 start가 이미 지났거나(=daysToStart < 0), PV>0이면 "지연"으로 보는 편이 일반적.
    // ─────────────────────────────────────────────
    if (evZeroish && !pvZeroish) {
        // start가 없더라도 PV>0이면 이미 '계획 진도'가 존재하므로 기본은 지연으로 본다.
        // 다만 "아직 착수 전인데 PV>0" 같은 이상데이터는 아래 갭 로직이 ON_TRACK로 만들 수 있어 혼선이 생길 수 있으니,
        // 여기서는 명확히 DELAY로 처리한다(실적 없음).
        return STATUS.DELAY;
    }

    // ─────────────────────────────────────────────
    // 4) PV/EV 갭 기반 판정 (핵심)
    //
    // pv가 없거나 0이면 "갭" 판단 근거가 부족하다.
    // - EV도 없으면: 일정 기반으로 PLANNED/IMPENDING 정도로만 판정
    // - EV가 있으면: ON_TRACK로 둔다(실적이 있으니 진행 중)
    // ─────────────────────────────────────────────
    if (pvZeroish) {
        if (!evZeroish) return STATUS.ON_TRACK;

        // EV도 없고 PV도 0/null인 경우:
        // - start가 있으면: 착수 전이면 PLANNED/IMPENDING, 착수 후(지났으면) IMPENDING(보수적) 또는 PLANNED 선택 가능
        if (start && daysToStart != null) {
            if (daysToStart > THRESHOLDS.IMPENDING_DAYS) return STATUS.PLANNED;
            if (daysToStart >= 0) return STATUS.IMPENDING;

            // 착수일이 지났는데도 PV/EV 모두 0 → "일정상 시작했어야 함" → 지연으로 보는 것이 더 자연스럽다.
            // 기존 코드가 IMPENDING였던 부분을 개선: 지연으로 처리
            return STATUS.DELAY;
        }

        // start도 없으면 근거 부족 → 예정으로 둠(기존보다 보수적)
        return STATUS.PLANNED;
    }

    // pv > 0 인 상태로 내려온 경우(여기까지 왔다는 것은 evZeroish==false 인 경우가 대부분)
    if (typeof ev === 'number') {
        const gap = (pv as number) - ev;

        // ON_TRACK:
        // - pv와 ev 차이가 아주 작거나
        // - ev가 pv를 초과(계획보다 앞섬)
        if (Math.abs(gap) < THRESHOLDS.ONTRACK_ABS_GAP || ev > (pv as number)) return STATUS.ON_TRACK;

        // ev < pv 인 경우(gap > 0):
        // gap이 작으면 '주의', 크면 '지연'
        if (gap < THRESHOLDS.RISK_GAP) return STATUS.RISK;

        return STATUS.DELAY;
    }

    // ─────────────────────────────────────────────
    // 5) EV가 숫자가 아닌데(pv는 >0), 여기까지 왔다면:
    //    → 사실상 "EV 없음 + PV>0" 케이스인데, 위에서 이미 DELAY로 리턴되도록 했음.
    //    안전망으로 DELAY.
    // ─────────────────────────────────────────────
    return STATUS.DELAY;
}
